import React, { Component } from "react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const CHART_COLORS = {
    primary: "#4e73df",
    success: "#1cc88a",
    info: "#36b9cc",
    warning: "#f6c23e",
    pending: "#e3e6f0",
};

class DashboardCharts extends Component {
    renderEmptyState(message) {
        return (
            <div className="dashboard-chart-empty text-center text-muted py-5">
                <i className="fas fa-chart-area fa-2x mb-2 d-block"></i>
                {message}
            </div>
        );
    }

    renderTrendChart(monthlyTrend) {
        if (!monthlyTrend || monthlyTrend.length === 0) {
            return this.renderEmptyState("No trend data available for the last 12 months.");
        }

        const chartData = monthlyTrend.map((item) => ({
            label: item.MonthLabel,
            totalLeads: item.TotalLeads,
            convertedLeads: item.ConvertedLeads,
        }));

        return (
            <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eaecf4" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-35} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ paddingTop: 12 }} />
                    <Area
                        type="monotone"
                        dataKey="totalLeads"
                        name="Total Leads"
                        stroke={CHART_COLORS.primary}
                        fill={CHART_COLORS.primary}
                        fillOpacity={0.15}
                        strokeWidth={2}
                    />
                    <Area
                        type="monotone"
                        dataKey="convertedLeads"
                        name="Converted Leads"
                        stroke={CHART_COLORS.success}
                        fill={CHART_COLORS.success}
                        fillOpacity={0.15}
                        strokeWidth={2}
                    />
                </AreaChart>
            </ResponsiveContainer>
        );
    }

    renderDonutChart(monthlyLeadCount, monthlyConvertedLeadCount) {
        const monthlyPendingLeads = Math.max(monthlyLeadCount - monthlyConvertedLeadCount, 0);
        const donutData = [
            { name: "Converted", value: monthlyConvertedLeadCount },
            { name: "Pending", value: monthlyPendingLeads },
        ];

        if (monthlyLeadCount <= 0) {
            return this.renderEmptyState("No monthly leads to display.");
        }

        return (
            <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                    <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={110}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) =>
                            percent > 0 ? `${name}: ${(percent * 100).toFixed(0)}%` : null
                        }
                    >
                        <Cell fill={CHART_COLORS.success} />
                        <Cell fill={CHART_COLORS.pending} />
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ paddingTop: 12 }} />
                </PieChart>
            </ResponsiveContainer>
        );
    }

    renderComparisonBarChart(kpi) {
        const barData = [
            {
                name: "Total Leads",
                Monthly: kpi.monthlyLeadCount,
                Yearly: kpi.yearlyLeadCount,
            },
            {
                name: "Converted",
                Monthly: kpi.monthlyConvertedLeadCount,
                Yearly: kpi.yearlyConvertedLeadCount,
            },
        ];

        const hasData = barData.some((row) => row.Monthly > 0 || row.Yearly > 0);
        if (!hasData) {
            return this.renderEmptyState("No comparison data available.");
        }

        return (
            <ResponsiveContainer width="100%" height={320}>
                <BarChart data={barData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eaecf4" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ paddingTop: 12 }} />
                    <Bar dataKey="Monthly" name="Monthly" fill={CHART_COLORS.primary} barSize={48} />
                    <Bar dataKey="Yearly" name="Yearly" fill={CHART_COLORS.info} barSize={48} />
                </BarChart>
            </ResponsiveContainer>
        );
    }

    render() {
        const { monthlyTrend, kpi } = this.props;

        return (
            <>
                <div className="row">
                    <div className="col-lg-8 mb-4">
                        <div className="card shadow mb-4">
                            <div className="card-header py-3">
                                <h6 className="m-0 font-weight-bold text-primary">Lead Trend (Last 12 Months)</h6>
                            </div>
                            <div className="card-body dashboard-chart-card-body">
                                {this.renderTrendChart(monthlyTrend)}
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4 mb-4">
                        <div className="card shadow mb-4">
                            <div className="card-header py-3">
                                <h6 className="m-0 font-weight-bold text-primary">Monthly Conversion</h6>
                            </div>
                            <div className="card-body dashboard-chart-card-body">
                                {this.renderDonutChart(kpi.monthlyLeadCount, kpi.monthlyConvertedLeadCount)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12 mb-4">
                        <div className="card shadow mb-4">
                            <div className="card-header py-3">
                                <h6 className="m-0 font-weight-bold text-primary">Monthly vs Yearly Comparison</h6>
                            </div>
                            <div className="card-body dashboard-chart-card-body">
                                {this.renderComparisonBarChart(kpi)}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default DashboardCharts;
